/**
 * Xident Admin API Client
 *
 * Envelope-aware fetch wrapper for the Go API at /admin/v1.
 * Response format: { success: bool, data: T | ErrorData, meta: { request_id, timestamp, pagination? } }
 */

import { useAuthStore } from '@/stores/authStore'
import { clearQueryCache } from '@/lib/queryClient'
import { buildQueryString } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Base URLs
// ---------------------------------------------------------------------------

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/admin/v1`
  : '/admin/v1'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ApiEnvelope<T> {
  success: boolean
  data: T
  error?: ApiErrorData
  meta?: {
    request_id?: string
    timestamp?: string
    pagination?: {
      page: number
      per_page: number
      total: number
      total_pages: number
    }
  }
}

interface ApiErrorData {
  code: string
  message: string
  details?: Record<string, string>
}

interface RequestOptions {
  method?: string
  body?: unknown
  headers?: Record<string, string>
}

// ---------------------------------------------------------------------------
// Error class
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string,
    public details?: Record<string, string>
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// ---------------------------------------------------------------------------
// Auth header injection
// ---------------------------------------------------------------------------

const PUBLIC_ENDPOINTS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/2fa/challenge',
  '/auth/passkey/authenticate/begin',
  '/auth/passkey/authenticate/finish',
]

function isPublicEndpoint(endpoint: string): boolean {
  return PUBLIC_ENDPOINTS.some((p) => endpoint.startsWith(p))
}

// ---------------------------------------------------------------------------
// Token refresh mutex — prevents concurrent refresh requests
// ---------------------------------------------------------------------------

let refreshPromise: Promise<boolean> | null = null

/**
 * Refreshes the access token. Uses a singleton promise so concurrent 401s
 * all await the same refresh attempt (prevents burst refresh calls).
 *
 * IMPORTANT: `refreshPromise` is cleared ONLY on success. If the refresh
 * fails, we keep the (resolved-to-false) promise around so concurrent
 * callers see the same outcome without re-triggering refresh against
 * already-invalid tokens (which would produce spurious logouts).
 */
async function tryRefreshToken(): Promise<boolean> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    const { refreshToken } = useAuthStore.getState()

    try {
      // credentials: 'include' lets the browser send the HttpOnly refresh
      // cookie on the refresh endpoint. In phase-1 dual-write we ALSO keep
      // posting the refresh_token in the body (if we still have it in
      // memory) so the backend accepts either source.
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(refreshToken ? { refresh_token: refreshToken } : {}),
      })

      if (!response.ok) return false

      const envelope: ApiEnvelope<{
        user: AdminUserResponse
        access_token: string
        refresh_token: string
      }> = await response.json()

      if (!envelope.success) return false

      // Phase-1 dual-write: backend may return tokens in body AND as cookies.
      // We mirror tokens into memory if present so Bearer-header fallback
      // code paths (CI tools, tests) keep working. Once the backend drops
      // the body in phase-2, these fields will be undefined and that's fine —
      // the cookies still authenticate us.
      const data = envelope.data
      if (data?.access_token && data?.refresh_token) {
        useAuthStore.getState().setTokens(data.access_token, data.refresh_token)
      }
      return true
    } catch {
      return false
    }
  })()

  // Only clear on SUCCESS. On failure, keep the resolved-false promise so
  // concurrent callers see the same outcome without re-triggering refresh.
  const result = await refreshPromise
  if (result) {
    refreshPromise = null
  }
  return result
}

// ---------------------------------------------------------------------------
// Core request function
// ---------------------------------------------------------------------------

async function requestRaw<T>(endpoint: string, options: RequestOptions = {}): Promise<ApiEnvelope<T>> {
  const { method = 'GET', body, headers = {} } = options

  const buildConfig = (): RequestInit => {
    const reqHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    }

    if (!isPublicEndpoint(endpoint)) {
      const token = useAuthStore.getState().accessToken
      if (token) {
        reqHeaders['Authorization'] = `Bearer ${token}`
      }
    }

    const config: RequestInit = {
      method,
      headers: reqHeaders,
      // credentials: 'include' causes the browser to attach the HttpOnly
      // auth cookies to same-site API requests. The Bearer header (set
      // above from in-memory state, if present) is retained as a fallback
      // for CLI / CI tooling and for phase-1 dual-write transition.
      credentials: 'include',
      signal: AbortSignal.timeout(30_000),
    }
    if (body) config.body = JSON.stringify(body)
    return config
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE}${endpoint}`, buildConfig())
  } catch (error) {
    if (error instanceof DOMException && error.name === 'TimeoutError') {
      throw new ApiError('Request timed out', 408, 'TIMEOUT_ERROR')
    }
    throw error
  }

  // Attempt silent token refresh on 401 (non-public endpoints)
  if (response.status === 401 && !isPublicEndpoint(endpoint)) {
    const refreshed = await tryRefreshToken()
    if (refreshed) {
      try {
        response = await fetch(`${API_BASE}${endpoint}`, buildConfig())
      } catch (error) {
        if (error instanceof DOMException && error.name === 'TimeoutError') {
          throw new ApiError('Request timed out', 408, 'TIMEOUT_ERROR')
        }
        throw error
      }
    }
  }

  // Handle 429 rate limiting
  if (response.status === 429) {
    const retryAfter = response.headers.get('Retry-After')
    const waitSeconds = retryAfter ? parseInt(retryAfter, 10) || 60 : 60
    throw new ApiError(
      `Too many requests. Please try again in ${waitSeconds} seconds.`,
      429,
      'RATE_LIMIT_EXCEEDED',
      { retry_after: String(waitSeconds) }
    )
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return { success: true, data: {} as T } as ApiEnvelope<T>
  }

  let envelope: ApiEnvelope<T | ApiErrorData>
  try {
    envelope = await response.json()
  } catch {
    throw new ApiError('Request failed', response.status, 'PARSE_ERROR')
  }

  // Handle errors
  if (!envelope.success) {
    const errorData = (envelope as { error?: ApiErrorData }).error

    // Auto-logout on 401 (after refresh already failed)
    if (response.status === 401 && !isPublicEndpoint(endpoint)) {
      clearQueryCache()
      useAuthStore.getState().logout()
      window.location.href = '/login'
    }

    throw new ApiError(
      errorData?.message || 'Request failed',
      response.status,
      errorData?.code || 'UNKNOWN_ERROR',
      errorData?.details
    )
  }

  return envelope as ApiEnvelope<T>
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const envelope = await requestRaw<T>(endpoint, options)
  return envelope.data
}

async function requestPaginated<T>(endpoint: string, options: RequestOptions = {}): Promise<PaginatedResponse<T>> {
  const envelope = await requestRaw<T[]>(endpoint, options)
  const items = Array.isArray(envelope.data) ? envelope.data : []
  const p = envelope.meta?.pagination
  return {
    items,
    total: p?.total ?? 0,
    page: p?.page ?? 1,
    per_page: p?.per_page ?? 20,
    total_pages: p?.total_pages ?? 0,
  }
}

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

// Auth
export interface LoginResponse {
  requires_2fa: boolean
  challenge_token?: string // Present only when requires_2fa=true
  access_token?: string
  refresh_token?: string
  user?: AdminUserResponse
}

export interface AuthResponse {
  user: AdminUserResponse
  access_token: string
  refresh_token: string
}

export interface AdminUserResponse {
  id: number
  email: string
  name: string
  role: string
  /**
   * RBAC role + permissions, resolved server-side from `admin_role_id`.
   *
   * Both are `omitempty` on the Go DTO (`adminUserDTO` in
   * `api/internal/api/handlers/admin/auth.go`), so they are ABSENT — not empty
   * — for an admin whose `admin_role_id` is NULL. `usePermissions` treats that
   * absence as "unknown", not "denied"; see the comment there.
   *
   * Note `/auth/me` does not include these fields; only login and the 2FA
   * challenge do. The admin panel never calls `/auth/me`, so the permission
   * list captured at login is the one that stays in the store.
   */
  role_id?: number
  permissions?: string[]
  two_factor_enabled: boolean
  last_login_at: string | null
  created_at: string
}

// Tenants — field names match database columns
export interface Tenant {
  id: number
  name: string
  email: string
  plan_tier: string
  is_active: boolean
  user_count: number
  project_count: number
  tags: string[]
  created_at: string
}

/**
 * A read-only dashboard grant for one tenant.
 *
 * `read_only` is reported by the server rather than assumed by the UI: the
 * guarantee is enforced in the API's dashboard middleware (every non-GET request
 * from this token is refused 403), and a client-side constant would be a second
 * claim about it that could drift from the enforcement.
 */
export interface ImpersonationGrant {
  token: string
  expires_at: string
  tenant_id: number
  tenant_name: string
  read_only: boolean
}

export interface TenantDetail extends Tenant {
  users: TenantUser[]
  projects: TenantProject[]
  total_verifications: number
  monthly_verifications: number
}

export interface TenantUser {
  id: number
  email: string
  name: string
  role: string
  is_active: boolean
  created_at: string
}

export interface TenantProject {
  id: number
  name: string
  public_key: string
  is_active: boolean
  total_verifications: number
  created_at: string
}

// Allowance UNIT grants (usage_credit_grants) — NOT the money credit_ledger
// above. This is the pool the Free tier's hard stop and every plan's
// Check/Verification meter actually decrement against; see api/migrations/
// 116_usage_allowance_grants.sql. `source` distinguishes what funded the
// grant: the monthly plan allowance, a purchased prepaid pack, an
// auto-topup claim, or (this feature) a manual admin_grant.
export interface UsageCreditGrant {
  id: number
  tenant_id: number
  unit_kind: 'check' | 'verification'
  source: 'plan_allowance' | 'purchase' | 'auto_topup' | 'admin_grant'
  period: string | null
  reason: string | null
  external_ref: string | null
  units_total: number
  units_remaining: number
  hard_stop: boolean
  granted_at: string
  expires_at: string | null
  created_at: string
  updated_at: string
}

// Users (dashboard users)
export interface DashboardUser {
  id: number
  email: string
  name: string
  role_id: number
  tenant_id: number
  is_active: boolean
  email_verified: boolean
  tags: string[]
  last_login_at: string | null
  created_at: string
}

// Analytics
export interface AnalyticsOverview {
  total_tenants: number
  total_users: number
  total_verifications: number
  active_today_tenants: number
  verifications_today: number
}

export interface TimeSeriesPoint {
  date: string
  count: number
}

export interface VerificationStatsResponse {
  total_count: number
  by_status: Record<string, number>
  time_series: TimeSeriesPoint[]
}

export interface PeriodStats {
  total_count: number
  by_status: Record<string, number>
  time_series: TimeSeriesPoint[]
  avg_per_day: number
  unique_tenants: number
}

export interface PeriodChanges {
  total_change: number
  completed_change: number
  avg_per_day_change: number
  unique_tenant_change: number
}

export interface FunnelStats {
  created: number
  in_progress: number
  completed: number
  failed: number
  canceled: number
  drop_off_rate: number
  success_rate: number
}

export interface ComparisonStats {
  current: PeriodStats
  previous: PeriodStats
  changes: PeriodChanges
  funnel: FunnelStats
}

export interface EmailProviderStat {
  provider: string
  sent: number
  failed: number
}

export interface EmailDeliveryFailure {
  provider: string
  template: string
  recipient: string
  error_message: string
  created_at: string
}

export interface EmailDeliveryStats {
  days: number
  total_sent: number
  total_failed: number
  fallback_count: number
  fallback_rate: number
  by_provider: EmailProviderStat[]
  recent_failures: EmailDeliveryFailure[]
}

// Dashboard KPIs
export type KPIRangeKey = 'today' | '24h' | '7d' | '30d' | '90d'

/**
 * One dashboard number with an optional period-over-period comparison.
 * `previous` and `change_pct` are BOTH null/absent when there is no comparable
 * prior period (the prior window predates the metric's data). A null `change_pct`
 * is the signal to render the value with no arrow and no percentage.
 */
export interface KPIMetric {
  value: number
  previous?: number | null
  change_pct?: number | null
}

export interface KPIRangeMeta {
  key: KPIRangeKey
  label: string
  from: string
  to: string
  prior_from: string
  prior_to: string
}

export interface DashboardKPIs {
  range: KPIRangeMeta
  // Verification
  sessions_created: KPIMetric
  sessions_completed: KPIMetric
  pass_rate: KPIMetric
  // Growth
  new_tenants: KPIMetric
  new_accounts: KPIMetric
  active_tenants: KPIMetric
  // Revenue & usage
  billable_events: KPIMetric
  revenue_eur: KPIMetric
  ocr_spend_usd: KPIMetric
  // Email & outreach
  emails_sent: KPIMetric
  email_fail_rate: KPIMetric
  outreach_opens: KPIMetric
  // Ops health
  review_queue_pending: KPIMetric
  support_tickets_open: KPIMetric
}

// Audit
export interface AuditLog {
  id: number
  admin_user_id: number
  admin_email: string
  action: string
  target_type: string
  target_id: string
  ip_address: string
  user_agent: string
  created_at: string
}

export interface AuditLogDetail extends AuditLog {
  before_snapshot: unknown
  after_snapshot: unknown
}

// Support (Go uses UUID strings for ticket/message IDs)
export interface SupportTicket {
  id: string
  tenant_id: number | null
  /**
   * NOTE: tenant_name and user_email are NOT sent by the API — models.SupportTicket
   * has no such fields, so these are always undefined. Declared here since before
   * this comment; the columns that render them have always been blank. Left in
   * place rather than deleted so the fix is a server change, not a silent removal
   * of the intent.
   */
  tenant_name: string
  user_email: string

  /**
   * Set only on contact_form tickets, where there is no tenant and no account —
   * the requester is a stranger who used the website contact form.
   */
  requester_name?: string
  requester_email?: string
  account_id: number | null
  subject: string
  status: string
  priority: string
  category: string
  source: string
  ai_handled: boolean
  assigned_to: string | null
  assigned_name: string | null
  created_at: string
  updated_at: string
  last_message_at: string | null
  last_message_preview: string | null
  unread_count_customer: number
  unread_count_staff: number
}

export interface SupportTicketDetail extends SupportTicket {
  category: string
  first_response_at: string | null
  resolved_at: string | null
  messages: TicketMessage[]
}

export interface TicketAttachment {
  key: string
  filename: string
  size: number
  content_type: string
  url: string
}

export interface TicketMessage {
  id: string
  message: string
  is_staff: boolean
  is_internal: boolean
  is_ai: boolean
  ai_confidence: number | null
  ai_escalated: boolean
  ai_model: string
  author_email: string
  author_name: string
  attachments?: TicketAttachment[]
  created_at: string
}

export interface PresignResponse {
  upload_url: string
  key: string
  public_url: string
  expires_at: string
}

// Jobs (River) — matches Go RiverJobStats struct (flat state counts)
export interface JobStats {
  available: number
  running: number
  completed: number
  retryable: number
  discarded: number
  cancelled: number
  pending: number
  scheduled: number
}

export interface Job {
  id: number
  kind: string
  state: string
  queue: string
  args: string
  errors: string
  attempt: number
  max_attempts: number
  created_at: string
  attempted_at: string | null
  finalized_at: string | null
}

// System
export interface SystemHealth {
  status: string
  database: string
  redis: string
  uptime: string
}

export interface DetailedSystemStatus {
  status: string
  database: {
    status: string
    open_connections: number
    in_use: number
    idle: number
    max_open_connections: number
    wait_count: number
    wait_duration: string
  }
  redis: {
    status: string
    used_memory?: string
    connected_clients?: string
  }
  queue: Record<string, number>
  version: string
}

// Notifications
export interface Notification {
  id: number
  admin_user_id: number
  admin_email: string
  title: string
  body: string
  recipient_type: string
  recipient_id: number | null
  recipient_tag: string
  is_published: boolean
  read_count: number
  created_at: string
}

export interface CreateNotificationRequest {
  title: string
  body: string
  recipient_type: string
  recipient_id?: number
  recipient_tag?: string
}

// Tags
export interface TagCount {
  tag: string
  count: number
}

// Accounts (portal / Xident ID users)
export interface AccountListItem {
  id: number
  email: string
  display_name: string
  avatar_url: string | null
  status: 'active' | 'suspended' | 'locked' | 'pending_deletion'
  email_verified: boolean
  id_verified: boolean
  age_brackets: number[]
  sites_count: number
  last_login_at: string | null
  created_at: string
}

export interface AccountOverview {
  id: number
  email: string
  display_name: string
  avatar_url: string | null
  bio: string | null
  date_of_birth: string | null
  language: string
  theme: string
  status: 'active' | 'suspended' | 'locked' | 'pending_deletion'
  email_verified: boolean
  email_verified_at: string | null
  id_verified: boolean
  id_verified_at: string | null
  is_active: boolean
  failed_login_count: number
  locked_until: string | null
  deletion_requested_at: string | null
  last_login_at: string | null
  created_at: string
  updated_at: string
  passkey_count: number
  session_count: number
  connection_count: number
  notification_count: number
  age_brackets: { age_bracket: number; method: string; confidence: number; verified_at: string; expires_at: string | null }[]
  recent_logins: { id: number; method: string; ip_address: string; location: string; success: boolean; failure_reason: string; created_at: string }[]
}

export interface AccountStats {
  total: number
  active: number
  suspended: number
  locked: number
  pending_deletion: number
  email_verified: number
  id_verified: number
}

// Account sub-resources. Field sets mirror the Go models one-for-one
// (`api/internal/domain/models/account_*.go`); fields tagged `json:"-"` there
// (credential blobs, refresh-token hashes, raw user agents) are deliberately
// absent here because the server never serialises them.
export interface AccountConnection {
  id: number
  account_id: number
  tenant_id: number
  first_connected_at: string
  last_used_at: string
  use_count: number
}

export interface AccountPasskey {
  id: number
  account_id: number
  sign_count: number
  name: string
  backup_eligible: boolean
  backup_state: boolean
  device_info: string
  platform: string
  browser: string
  created_at: string
  last_used_at: string | null
}

export interface AccountSession {
  id: number
  account_id: number
  ip_address: string
  device_name: string
  platform: string
  browser: string
  location: string
  is_current: boolean
  created_at: string
  last_active_at: string
  expires_at: string
}

export interface AccountLoginEvent {
  id: number
  account_id: number
  method: string
  ip_address: string
  location: string
  success: boolean
  failure_reason?: string
  created_at: string
}

export interface AccountNotification {
  id: number
  account_id: number
  title: string
  body: string
  type: string
  is_read: boolean
  metadata: Record<string, unknown>
  created_at: string
}

// Content Categories
export interface ContentCategory {
  id: number
  slug: string
  name: string
  description?: string
  icon?: string
  default_min_age: number
  default_methods: string[]
  default_ocr_mandatory: boolean
  is_active: boolean
  sort_order: number
  country_rule_count: number
  created_at: string
  updated_at: string
}

export interface CountryRule {
  id: number
  country_alpha2: string
  /**
   * FK to the owning content category.
   *
   * This replaced a free-text `content_category` slug on the backend
   * (`api/internal/domain/models/country_content_rule.go`) so renaming a
   * category can never orphan a rule. The admin type still declared the old
   * slug — a field the server has not sent for some time. Fixtures shaped like
   * the real DTO now live in `CountryRuleDialog.test.tsx`, so the next rename
   * fails a test rather than silently drifting.
   */
  content_category_id: number
  min_age: number
  min_age_bracket: number
  methods?: string[]
  ocr_mandatory: boolean
  regime: string
  challenge_age?: number
  law_reference?: string
  notes?: string
  created_at: string
  updated_at: string
}

// Pagination wrapper
export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

// ---------------------------------------------------------------------------
// API endpoints
// ---------------------------------------------------------------------------

// Passkey types
export interface PasskeyBeginResponse {
  session_id: string
  options: unknown // PublicKeyCredentialRequestOptionsJSON
}

export interface PasskeyRegistrationBeginResponse {
  session_id: number
  options: unknown // PublicKeyCredentialCreationOptionsJSON
}

export interface Passkey {
  id: number
  name: string
  platform: string
  browser: string
  device_info: string
  backup_eligible: boolean
  backup_state: boolean
  sign_count: number
  created_at: string
  last_used_at: string | null
}

export const auth = {
  login: (data: { email: string; password: string; turnstile_token?: string }) =>
    request<LoginResponse>('/auth/login', { method: 'POST', body: data }),

  twoFactorChallenge: (data: { challenge_token: string; code: string }) =>
    request<AuthResponse>('/auth/2fa/challenge', { method: 'POST', body: data }),

  refresh: (data: { refresh_token: string }) =>
    request<AuthResponse>('/auth/refresh', { method: 'POST', body: data }),

  me: () => request<AdminUserResponse>('/auth/me'),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  forgotPassword: (data: { email: string; turnstile_token?: string }) =>
    request<{ message: string }>('/auth/forgot-password', { method: 'POST', body: data }),

  resetPassword: (data: { token: string; password: string }) =>
    request<{ message: string }>('/auth/reset-password', { method: 'POST', body: data }),

  passkey: {
    authenticateBegin: (email?: string) =>
      request<PasskeyBeginResponse>('/auth/passkey/authenticate/begin', {
        method: 'POST',
        body: email ? { email } : {},
      }),

    // The credential is the WHOLE body -- go-webauthn parses the raw request
    // body as the assertion, so it cannot share the body with anything else.
    // The session id therefore rides in X-Session-ID, which the handler reads
    // (and 400s on when missing). Same shape as registerFinish below.
    authenticateFinish: (data: { session_id: string; credential: unknown }) =>
      request<AuthResponse>('/auth/passkey/authenticate/finish', {
        method: 'POST',
        body: data.credential,
        headers: {
          'X-Session-ID': String(data.session_id),
        },
      }),

    registerBegin: () =>
      request<PasskeyRegistrationBeginResponse>('/auth/passkey/register/begin', { method: 'POST' }),

    registerFinish: (data: { session_id: number; name?: string }, credential: unknown) =>
      request<Passkey>('/auth/passkey/register/finish', {
        method: 'POST',
        body: credential,
        headers: {
          'X-Session-ID': String(data.session_id),
          ...(data.name ? { 'X-Passkey-Name': data.name } : {}),
        },
      }),

    list: () => request<Passkey[]>('/auth/passkeys'),

    update: (id: number, data: { name: string }) =>
      request<{ message: string }>(`/auth/passkeys/${id}`, { method: 'PATCH', body: data }),

    delete: (id: number) =>
      request<{ message: string }>(`/auth/passkeys/${id}`, { method: 'DELETE' }),
  },

  twoFactor: {
    setup: () => request<{ secret: string; qr_uri: string }>('/auth/2fa/setup', { method: 'POST' }),
    verify: (data: { code: string }) =>
      request<{ success: boolean }>('/auth/2fa/verify', { method: 'POST', body: data }),
    disable: (data: { code: string }) =>
      request<void>('/auth/2fa/disable', { method: 'POST', body: data }),
    status: () => request<{ enabled: boolean }>('/auth/2fa/status'),
  },
}

/** One published API version, with whether each lifecycle transition is allowed. */
export interface AdminApiVersion {
  version: string
  released_at: string
  status: 'current' | 'deprecated' | 'sunset'
  deprecated_at?: string
  sunset_at?: string
  /** How many API keys are pinned here — the number that decides whether deprecating is safe. */
  pinned_keys: number
  can_deprecate: boolean
  /** Why Deprecate is unavailable. A disabled button with no reason becomes a support question. */
  deprecate_blocked_reason?: string
  can_sunset: boolean
  sunset_blocked_reason?: string
}

export const apiVersionsAdmin = {
  list: () => request<AdminApiVersion[]>('/api-versions'),
  deprecate: (version: string, sunsetAt?: string) =>
    request<{ version: string; status: string; sunset_at: string }>(
      `/api-versions/${version}/deprecate`,
      { method: 'POST', body: sunsetAt ? { sunset_at: sunsetAt } : {} },
    ),
  sunset: (version: string) =>
    request<{ version: string; status: string }>(`/api-versions/${version}/sunset`, {
      method: 'POST',
    }),
}

// ---------------------------------------------------------------------------
// Runtime settings and tenant flags (migration 167). A setting is a typed
// definition with a default; an admin may store a global value, and for
// tenant-scoped keys a per-tenant override. `effective` is what the code
// reads: override, then global, then default.

export type SettingValueType = 'bool' | 'int' | 'float' | 'string' | 'duration' | 'json'
export type SettingScope = 'global' | 'tenant' | 'both'

export interface AdminSetting {
  key: string
  value_type: SettingValueType
  scope: SettingScope
  category: string
  description: string
  admin_editable: boolean
  /** JSON text of the default. */
  default_value: unknown
  /** What is stored at this scope, or null when nothing is. */
  value: unknown
  updated_by?: number
  updated_at?: string
  /** The value the code reads for this scope. */
  effective: unknown
}

export interface UploadPurgeResult {
  cleaned: number
  hours: number
  kept_tenants: number
}

export const settingsAdmin = {
  list: () => request<AdminSetting[]>('/settings'),
  set: (key: string, value: unknown) =>
    request<AdminSetting>(`/settings/${key}`, { method: 'PUT', body: { value } }),
  tenantList: (tenantId: number) => request<AdminSetting[]>(`/tenants/${tenantId}/settings`),
  tenantSet: (tenantId: number, key: string, value: unknown) =>
    request<AdminSetting>(`/tenants/${tenantId}/settings/${key}`, { method: 'PUT', body: { value } }),
  tenantClear: (tenantId: number, key: string) =>
    request<AdminSetting>(`/tenants/${tenantId}/settings/${key}`, { method: 'DELETE' }),
  purgeUploads: () => request<UploadPurgeResult>('/settings/retention/purge-uploads', { method: 'POST' }),
}

// ---------------------------------------------------------------------------
// Document types per country (migration 169). Which of ID card, driving
// licence and passport a country may present in the verify widget. A country
// never edited shows all three enabled (a missing row means enabled on the
// server). The keys are the widget's DocType strings, so one shape serves
// the list row, the PUT body and the picker.

export interface DocumentTypeFlags {
  id_card: boolean
  drivers_license: boolean
  passport: boolean
}

export interface CountryDocumentTypes extends DocumentTypeFlags {
  /** ISO 3166-1 alpha-2, uppercase. */
  code: string
  name: string
}

export const countryDocumentTypes = {
  list: () => request<CountryDocumentTypes[]>('/countries/document-types'),
  /** All three flags are required by the API; a partial body is a 400. */
  set: (code: string, flags: DocumentTypeFlags) =>
    request<CountryDocumentTypes>(`/countries/${encodeURIComponent(code)}/document-types`, { method: 'PUT', body: flags }),
}

// ---------------------------------------------------------------------------
// Cross-tenant lists (2026-09-12): what the tenant dashboard shows per
// website, across every tenant.

export interface AdminVerificationRow {
  id: number
  token: string
  tenant_id: number
  tenant_name: string
  api_key_id: number
  website_name: string
  status: string
  verification_type: string
  country_name: string
  mode: string
  ocr_task_id: string
  created_at: string
  completed_at: string | null
}

export interface AdminVerificationDetail {
  id: number
  token: string
  tenant_id: number
  api_key_id: number
  status: string
  verification_type: string
  country_code: string
  country_name: string
  regime: string
  mode: string
  external_user_id?: string
  handoff_device?: string
  ocr_task_id?: string
  created_at: string
  completed_at: string | null
  extracted?: Record<string, unknown>
  face_match?: Record<string, unknown>
  age_result?: Record<string, unknown>
  /** Presigned links keyed by file type; only objects still in storage. */
  image_urls?: Record<string, string>
  images_expire_at?: string
  images_missing: boolean
}

export interface AdminWebhookDeliveryRow {
  id: number
  webhook_id: number
  tenant_id: number
  tenant_name: string
  api_key_id: number
  website_name: string
  event_id: string
  event_type: string
  session_id: number | null
  url: string
  api_version: string
  attempt_number: number
  status_code: number | null
  response_time_ms: number | null
  success: boolean
  error_message: string
  payload: string
  response_body: string
  occurred_at: string
  next_retry_at: string | null
}

export interface AdminWebsite {
  id: number
  tenant_id: number
  tenant_name: string
  name: string
  is_active: boolean
}

export interface AdminVerificationFilters {
  tenant_id?: number
  api_key_id?: number
  status?: string
  start_date?: string
  end_date?: string
  page?: number
  per_page?: number
}

export interface AdminDeliveryFilters extends AdminVerificationFilters {
  webhook_id?: number
  event_type?: string
  success?: 'true' | 'false'
}

export const adminVerifications = {
  list: (params: AdminVerificationFilters = {}) =>
    requestPaginated<AdminVerificationRow>(`/verifications${buildQueryString({ ...params })}`),
  get: (id: number) => request<AdminVerificationDetail>(`/verifications/${id}`),
}

export const adminWebhookDeliveries = {
  list: (params: AdminDeliveryFilters = {}) =>
    requestPaginated<AdminWebhookDeliveryRow>(`/webhook-deliveries${buildQueryString({ ...params })}`),
}

export const adminWebsites = {
  list: (tenantId?: number) => request<AdminWebsite[]>(`/websites${buildQueryString({ tenant_id: tenantId })}`),
}

export const tenants = {
  list: (params?: { page?: number; per_page?: number; search?: string; start_date?: string; end_date?: string }) =>
    requestPaginated<Tenant>(`/tenants${buildQueryString(params ?? {})}`),

  get: (id: number) => request<TenantDetail>(`/tenants/${id}`),

  suspend: (id: number) =>
    request<void>(`/tenants/${id}/suspend`, { method: 'PATCH' }),

  unsuspend: (id: number) =>
    request<void>(`/tenants/${id}/unsuspend`, { method: 'PATCH' }),

  /**
   * Mint a READ-ONLY dashboard token for this tenant.
   *
   * The admin panel cannot show a tenant's verification sessions — the capability
   * does not exist here — so instead of mirroring the dashboard's pages (a second
   * copy that drifts), an admin borrows the real dashboard read-only.
   *
   * POST, not GET: it is audited by the API's mutation audit middleware, and
   * minting a credential is not a safe/idempotent read however it feels.
   */
  impersonate: (id: number) =>
    request<ImpersonationGrant>(`/tenants/${id}/impersonate`, { method: 'POST' }),

  // Grant account credit (EUR cents). In the metered billing model, credit offsets
  // usage charges before Lago — i.e. it is how you give a tenant free usage.
  grantCredits: (id: number, body: { amount_cents: number; reason: string; expires_at?: string }) =>
    request<{ balance_cents: number }>(`/tenants/${id}/credits`, { method: 'POST', body }),

  getCredits: (id: number) =>
    request<{ balance_cents: number }>(`/tenants/${id}/credits`),

  // Grant allowance UNITS (usage_credit_grants, source=admin_grant) — the
  // pool the Free tier's hard stop and every plan's Check/Verification meter
  // actually consult. Deliberately a SEPARATE call from grantCredits above:
  // the two top up different tables and are not interchangeable (an admin
  // issuing a money credit while meaning a Verification allowance is the
  // incident this endpoint exists to prevent).
  grantUnits: (
    id: number,
    body: {
      unit_kind: 'check' | 'verification'
      units: number
      reason: string
      expires_at?: string
      external_ref?: string
    }
  ) => request<UsageCreditGrant>(`/tenants/${id}/unit-grants`, { method: 'POST', body }),

  listUnitGrants: (id: number) => request<UsageCreditGrant[]>(`/tenants/${id}/unit-grants`),
}

export const users = {
  listDashboard: (params?: { page?: number; per_page?: number; search?: string; start_date?: string; end_date?: string }) =>
    requestPaginated<DashboardUser>(`/users/dashboard${buildQueryString(params ?? {})}`),

  getDashboard: (id: number) => request<DashboardUser>(`/users/dashboard/${id}`),

  suspend: (id: number) =>
    request<void>(`/users/dashboard/${id}/suspend`, { method: 'PATCH' }),

  unsuspend: (id: number) =>
    request<void>(`/users/dashboard/${id}/unsuspend`, { method: 'PATCH' }),

  /**
   * Email this member a password-reset link.
   *
   * The admin starts the flow; the member completes it. No credential comes
   * back in the response and there is no "set a temporary password" here on
   * purpose — that would put a live secret in an operator's hands and in
   * whatever channel they relayed it through.
   *
   * Exists because on 2026-08-31 a locked-out customer could not be helped at
   * all: the only route back in was the self-service email, and that flow was
   * broken. Recovering one account required shipping a release.
   */
  sendPasswordReset: (id: number) =>
    request<{ message: string }>(`/users/dashboard/${id}/send-password-reset`, { method: 'POST' }),
}

export const accounts = {
  stats: () => request<AccountStats>('/accounts/stats'),

  list: (params?: { page?: number; per_page?: number; search?: string; status?: string; verified?: string; start_date?: string; end_date?: string }) =>
    requestPaginated<AccountListItem>(`/accounts${buildQueryString(params ?? {})}`),

  get: (id: number) => request<AccountOverview>(`/accounts/${id}`),

  connections: (id: number, params?: { page?: number; per_page?: number }) =>
    requestPaginated<AccountConnection>(`/accounts/${id}/connections${buildQueryString(params ?? {})}`),

  passkeys: (id: number) => request<AccountPasskey[]>(`/accounts/${id}/passkeys`),

  sessions: (id: number) => request<AccountSession[]>(`/accounts/${id}/sessions`),

  loginHistory: (id: number, params?: { page?: number; per_page?: number }) =>
    requestPaginated<AccountLoginEvent>(`/accounts/${id}/login-history${buildQueryString(params ?? {})}`),

  notifications: (id: number, params?: { page?: number; per_page?: number }) =>
    requestPaginated<AccountNotification>(`/accounts/${id}/notifications${buildQueryString(params ?? {})}`),

  suspend: (id: number) => request<void>(`/accounts/${id}/suspend`, { method: 'PATCH' }),
  unsuspend: (id: number) => request<void>(`/accounts/${id}/unsuspend`, { method: 'PATCH' }),
  unlock: (id: number) => request<void>(`/accounts/${id}/unlock`, { method: 'PATCH' }),
  forceLogout: (id: number) => request<void>(`/accounts/${id}/force-logout`, { method: 'POST' }),
  delete: (id: number) => request<void>(`/accounts/${id}`, { method: 'DELETE' }),
}

export const dashboard = {
  kpis: (range: KPIRangeKey) =>
    request<DashboardKPIs>(`/dashboard/kpis${buildQueryString({ range })}`),
}

export const analytics = {
  overview: () => request<AnalyticsOverview>('/analytics/overview'),

  verifications: (params?: { days?: number }) =>
    request<VerificationStatsResponse>(`/analytics/verifications${buildQueryString(params ?? {})}`),

  comparison: (params?: { days?: number }) =>
    request<ComparisonStats>(`/analytics/comparison${buildQueryString(params ?? {})}`),

  emailDelivery: (params?: { days?: number }) =>
    request<EmailDeliveryStats>(`/analytics/email-delivery${buildQueryString(params ?? {})}`),
}

export const audit = {
  list: (params?: {
    page?: number
    per_page?: number
    admin_user_id?: number
    action?: string
    target_type?: string
    start_date?: string
    end_date?: string
  }) => requestPaginated<AuditLog>(`/audit${buildQueryString(params ?? {})}`),

  get: (id: number) => request<AuditLogDetail>(`/audit/${id}`),
}

export const support = {
  list: (params?: { page?: number; per_page?: number; status?: string; source?: string; start_date?: string; end_date?: string }) =>
    requestPaginated<SupportTicket>(`/support/tickets${buildQueryString(params ?? {})}`),

  get: (id: string) => request<SupportTicketDetail>(`/support/tickets/${id}`),

  assign: (id: string, data: { admin_user_id: number }) =>
    request<void>(`/support/tickets/${id}/assign`, { method: 'PATCH', body: data }),

  reply: (id: string, data: {
    message: string;
    attachments?: { key: string; filename: string; size: number; content_type: string }[];
  }) =>
    request<{ message: string }>(`/support/tickets/${id}/reply`, { method: 'POST', body: data }),

  internalNote: (id: string, data: {
    message: string;
    attachments?: { key: string; filename: string; size: number; content_type: string }[];
  }) =>
    request<{ message: string }>(`/support/tickets/${id}/note`, { method: 'POST', body: data }),

  presignAttachment: (id: string, data: { filename: string; content_type: string; size: number }) =>
    request<PresignResponse>(`/support/tickets/${id}/attachments/presign`, { method: 'POST', body: data }),

  changeStatus: (id: string, data: { status: string }) =>
    request<void>(`/support/tickets/${id}/status`, { method: 'PATCH', body: data }),
}

export const jobs = {
  stats: () => request<JobStats>('/jobs/stats'),

  list: (params?: { page?: number; per_page?: number; state?: string; kind?: string; start_date?: string; end_date?: string }) =>
    requestPaginated<Job>(`/jobs${buildQueryString(params ?? {})}`),

  get: (id: number) => request<Job>(`/jobs/${id}`),

  retry: (id: number) =>
    request<{ message: string }>(`/jobs/${id}/retry`, { method: 'PATCH' }),

  cancel: (id: number) =>
    request<{ message: string }>(`/jobs/${id}/cancel`, { method: 'PATCH' }),
}

export const system = {
  health: () => request<SystemHealth>('/system/health'),

  status: () => request<DetailedSystemStatus>('/system/status'),

  clearCache: () => request<{ message: string }>('/system/cache/clear', { method: 'POST' }),
}

export const notifications = {
  list: (params?: { page?: number; per_page?: number }) =>
    requestPaginated<Notification>(`/notifications${buildQueryString(params ?? {})}`),

  get: (id: number) => request<Notification>(`/notifications/${id}`),

  create: (data: CreateNotificationRequest) =>
    request<Notification>('/notifications', { method: 'POST', body: data }),

  delete: (id: number) =>
    request<{ message: string }>(`/notifications/${id}`, { method: 'DELETE' }),
}

export const tags = {
  tenantTags: () => request<string[]>('/tags/tenants'),

  memberTags: () => request<string[]>('/tags/members'),

  usedTenantTags: () => request<TagCount[]>('/tags/tenants/used'),

  usedMemberTags: () => request<TagCount[]>('/tags/members/used'),

  updateTenantTags: (tenantId: number, tags: string[]) =>
    request<{ tags: string[] }>(`/tenants/${tenantId}/tags`, { method: 'PATCH', body: { tags } }),

  updateMemberTags: (userId: number, tags: string[]) =>
    request<{ tags: string[] }>(`/users/dashboard/${userId}/tags`, { method: 'PATCH', body: { tags } }),
}

// ---------------------------------------------------------------------------
// Webhooks (admin — cross-tenant)
// ---------------------------------------------------------------------------

export interface Webhook {
  id: number
  tenant_id: number
  api_key_id: number
  url: string
  secret: string
  description: string
  events: string[]
  is_active: boolean
  failure_count: number
  last_success_at: string | null
  last_failure_at: string | null
  last_tested_at: string | null
  created_at: string
  updated_at: string
}

export interface WebhookDelivery {
  id: number
  webhook_id: number
  event_id: string
  event_type: string
  token: string | null
  payload: Record<string, unknown>
  attempt_number: number
  status_code: number | null
  response_body: string | null
  response_time_ms: number | null
  success: boolean
  error_message: string | null
  occurred_at: string
  next_retry_at: string | null
}

export const adminWebhooks = {
  list: (params?: { page?: number; per_page?: number; tenant_id?: number }) =>
    requestPaginated<Webhook>(`/webhooks${buildQueryString(params ?? {})}`),

  get: (id: number) => request<Webhook>(`/webhooks/${id}`),

  deliveries: (id: number, params?: { page?: number; per_page?: number }) =>
    requestPaginated<WebhookDelivery>(`/webhooks/${id}/deliveries${buildQueryString(params ?? {})}`),

  resendDelivery: (webhookId: number, deliveryId: number) =>
    request<{ message: string }>(`/webhooks/${webhookId}/deliveries/${deliveryId}/resend`, { method: 'POST' }),
}

// ---------------------------------------------------------------------------
// Billing Audit (admin — cross-tenant)
// ---------------------------------------------------------------------------

export interface UsageEvent {
  id: number
  tenant_id: number
  api_key_id: number
  token: string | null
  event_type: string
  occurred_at: string
  success: boolean
  is_billable: boolean
  plan_tier: string
  unit_price_cents: number
  total_price_cents: number
  billing_period: string
  deduplication_key: string
  record_hash: string
}

export interface BillingPeriod {
  id: number
  tenant_id: number
  period: string
  status: string
  event_count: number
  total_cents: number
  opened_at: string
  closed_at: string | null
}

export interface UsageSummary {
  total_events: number
  billable_events: number
  total_revenue_cents: number
  by_type: Record<string, { count: number; revenue_cents: number }>
}

export interface ApiRequestLog {
  id: number
  request_id: string
  tenant_id: number
  api_key_id: number
  token: string | null
  method: string
  path: string
  response_code: number
  latency_ms: number
  occurred_at: string
}

export const billingAudit = {
  usageEvents: (params?: { page?: number; per_page?: number; tenant_id?: number; event_type?: string; billing_period?: string; billable?: boolean }) =>
    requestPaginated<UsageEvent>(`/billing/usage-events${buildQueryString(params ?? {})}`),

  usageSummary: (params?: { tenant_id?: number; billing_period?: string }) =>
    request<UsageSummary>(`/billing/usage-summary${buildQueryString(params ?? {})}`),

  requestLogs: (params?: { page?: number; per_page?: number; tenant_id?: number }) =>
    requestPaginated<ApiRequestLog>(`/billing/request-logs${buildQueryString(params ?? {})}`),

  billingPeriods: (params?: { page?: number; per_page?: number; tenant_id?: number }) =>
    requestPaginated<BillingPeriod>(`/billing/periods${buildQueryString(params ?? {})}`),

  tenantUsageEvents: (tenantId: number, params?: { page?: number; per_page?: number; billing_period?: string }) =>
    requestPaginated<UsageEvent>(`/tenants/${tenantId}/usage-events${buildQueryString(params ?? {})}`),
}

// ---------------------------------------------------------------------------
// Billing Plans (Lago management)
// ---------------------------------------------------------------------------

export interface LagoPlanCharge {
  lago_id: string
  billable_metric_id: string
  charge_model: string
  /**
   * Forwarded from Lago verbatim, so the shape follows charge_model and the
   * numeric fields arrive as a number OR a string depending on the endpoint.
   * A `graduated` charge (Basic's Check ladder) has neither amount nor
   * free_units — read these through describeCharge() in PlansPage, which
   * handles every model.
   */
  properties: {
    amount?: string
    free_units?: number | string
    package_size?: number | string
    graduated_ranges?: {
      from_value: number | string
      to_value: number | string | null
      per_unit_amount: string
      flat_amount?: string
    }[]
  }
  billable_metric?: LagoBillableMetric
}

export interface LagoPlan {
  lago_id: string
  name: string
  code: string
  description: string
  amount_cents: number
  amount_currency: string
  interval: string
  pay_in_advance: boolean
  trial_period: number
  charges: LagoPlanCharge[]
  created_at: string
}

export interface LagoBillableMetric {
  lago_id: string
  name: string
  code: string
  description: string
  field_name: string
  created_at: string
}

export interface LagoPlanUpdate {
  name?: string
  description?: string
  amount_cents?: number
  charges?: {
    id?: string
    charge_model?: string
    properties: {
      amount?: string
      free_units?: string
    }
  }[]
}

export const billingPlans = {
  listPlans: () => request<LagoPlan[]>('/billing/plans'),

  getPlan: (code: string) => request<LagoPlan>(`/billing/plans/${code}`),

  updatePlan: (code: string, data: LagoPlanUpdate) =>
    request<LagoPlan>(`/billing/plans/${code}`, { method: 'PUT', body: data }),

  listMetrics: () => request<LagoBillableMetric[]>('/billing/metrics'),

  changeTenantPlan: (tenantId: number, planCode: string) =>
    request<{ tenant_id: number; new_plan: string; subscription_id: string }>(
      `/billing/tenants/${tenantId}/plan`,
      { method: 'POST', body: { plan_code: planCode } }
    ),
}

// ---------------------------------------------------------------------------
// Waitlist
// ---------------------------------------------------------------------------

export interface WaitlistEntry {
  id: number
  email: string
  ip_address: string
  user_agent: string
  confirmed: boolean
  notified: boolean
  created_at: string
  updated_at: string
}

export interface WaitlistStats {
  total: number
  today: number
  this_week: number
}

export const waitlist = {
  list: (params?: { page?: number; per_page?: number; search?: string }) =>
    requestPaginated<WaitlistEntry>(`/waitlist${buildQueryString(params ?? {})}`),

  stats: () => request<WaitlistStats>('/waitlist/stats'),

  exportCSV: async () => {
    // Cookie does the auth work; keep Bearer header as fallback for tooling.
    const token = useAuthStore.getState().accessToken
    const res = await fetch(`${API_BASE}/waitlist/export`, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) throw new ApiError('Export failed', res.status, 'EXPORT_ERROR')
    return res.blob()
  },

  sendAnnouncement: (data: { subject: string; body: string }) =>
    request<{ emails_queued: number }>('/waitlist/announce', { method: 'POST', body: data }),

  delete: (id: number) =>
    request<{ message: string }>(`/waitlist/${id}`, { method: 'DELETE' }),
}

// ---------------------------------------------------------------------------
// OAuth Admin (cross-tenant)
// ---------------------------------------------------------------------------

export interface OAuthClientAdmin {
  id: string
  tenant_id: number
  client_id: string
  name: string
  description: string
  status: string
  allowed_scopes: string[]
  redirect_uris: string[]
  created_at: string
  updated_at: string
}

export interface OAuthStats {
  total_clients: number
  active_clients: number
}

export const oauthAdmin = {
  listClients: (params?: { page?: number; per_page?: number }) =>
    requestPaginated<OAuthClientAdmin>(`/oauth/clients${buildQueryString(params ?? {})}`),

  getClient: (id: string) => request<OAuthClientAdmin>(`/oauth/clients/${id}`),

  suspendClient: (id: string) =>
    request<{ status: string }>(`/oauth/clients/${id}/suspend`, { method: 'POST' }),

  unsuspendClient: (id: string) =>
    request<{ status: string }>(`/oauth/clients/${id}/unsuspend`, { method: 'POST' }),

  getStats: () => request<OAuthStats>('/oauth/stats'),
}

// ---------------------------------------------------------------------------
// Marketing outreach
// ---------------------------------------------------------------------------

export interface MarketingCampaign {
  id: number
  name: string
  status: string
  default_template_id?: number | null
  daily_send_cap: number
  leads_count: number
  sent_count: number
  opened_count: number
  clicked_count: number
  created_at: string
  updated_at: string
}

export interface MarketingTemplate {
  id: number
  name: string
  segment_key: string
  subject_prompt: string
  body_prompt: string
  offer_text: string
  offer_enabled: boolean
  active: boolean
  sent_count: number
  opened_count: number
  clicked_count: number
  created_at: string
  updated_at: string
}

export interface MarketingLead {
  id: number
  campaign_id: number
  domain: string
  company_name: string
  contact_email: string
  country: string
  industry: string
  current_av_method: string
  size_segment: string
  company_summary: string
  personalization_hook: string
  confidence: string
  do_not_contact: boolean
  status: string
}

export interface MarketingEmail {
  id: number
  lead_id: number
  campaign_id: number
  template_id?: number | null
  subject: string
  preheader: string
  body_html: string
  body_text: string
  status: string
  provider_message_id: string
  sent_at?: string | null
  open_count: number
  click_count: number
  error?: string
  created_at: string
  updated_at: string
}

export interface MarketingCampaignStats {
  campaign_id: number
  sent: number
  unique_opens: number
  unique_clicks: number
  bounces: number
  unsubscribes: number
  open_rate: number
  click_rate: number
}

export interface MarketingImportResult {
  total_rows: number
  added: number
  skipped_existing: number
  duplicates_in_file: number
  excluded: number
  errors: { row: number; message: string }[]
}

export interface MarketingSuppression {
  id: number
  email_address: string
  reason: string
  created_at: string
}

export const marketingAdmin = {
  // Campaigns
  listCampaigns: (params?: { page?: number; per_page?: number }) =>
    requestPaginated<MarketingCampaign>(`/marketing/campaigns${buildQueryString(params ?? {})}`),
  getCampaign: (id: number) => request<MarketingCampaign>(`/marketing/campaigns/${id}`),
  createCampaign: (data: { name: string; default_template_id?: number; daily_send_cap?: number }) =>
    request<MarketingCampaign>('/marketing/campaigns', { method: 'POST', body: data }),
  updateCampaign: (
    id: number,
    data: Partial<{ name: string; status: string; default_template_id: number; daily_send_cap: number }>,
  ) => request<MarketingCampaign>(`/marketing/campaigns/${id}`, { method: 'PUT', body: data }),
  deleteCampaign: (id: number) =>
    request<{ status: string }>(`/marketing/campaigns/${id}`, { method: 'DELETE' }),
  listLeads: (id: number, params?: { page?: number; per_page?: number; status?: string }) =>
    requestPaginated<MarketingLead>(`/marketing/campaigns/${id}/leads${buildQueryString(params ?? {})}`),
  campaignStats: (id: number) => request<MarketingCampaignStats>(`/marketing/campaigns/${id}/stats`),
  generateDrafts: (id: number, data: { template_id: number; limit?: number }) =>
    request<{ status: string }>(`/marketing/campaigns/${id}/generate-drafts`, { method: 'POST', body: data }),
  sendCampaign: (id: number) =>
    request<{ queued: number }>(`/marketing/campaigns/${id}/send`, { method: 'POST' }),

  // CSV import — multipart, so it bypasses the JSON `request` helper.
  importCSV: async (id: number, file: File): Promise<MarketingImportResult> => {
    // Raw fetch (multipart can't go through the JSON `request` helper), so we
    // replicate requestRaw's 401 recovery: silent refresh + one retry, else
    // clear cache + logout + redirect — keeping the documented auth invariant.
    const doFetch = () => {
      const fd = new FormData()
      fd.append('file', file)
      return fetch(`${API_BASE}/marketing/campaigns/${id}/import`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      })
    }
    let res = await doFetch()
    if (res.status === 401) {
      const refreshed = await tryRefreshToken()
      if (refreshed) {
        res = await doFetch()
      }
      if (res.status === 401) {
        clearQueryCache()
        useAuthStore.getState().logout()
        window.location.href = '/login'
      }
    }
    const envelope = await res.json().catch(() => ({ success: false }))
    if (!envelope.success) {
      throw new ApiError(
        envelope.error?.message || 'Import failed',
        res.status,
        envelope.error?.code || 'IMPORT_FAILED',
        envelope.error?.details,
      )
    }
    return envelope.data as MarketingImportResult
  },

  // Templates
  listTemplates: (params?: { page?: number; per_page?: number }) =>
    requestPaginated<MarketingTemplate>(`/marketing/templates${buildQueryString(params ?? {})}`),
  listActiveTemplates: () => request<MarketingTemplate[]>('/marketing/templates/active'),
  getTemplate: (id: number) => request<MarketingTemplate>(`/marketing/templates/${id}`),
  createTemplate: (data: Partial<MarketingTemplate>) =>
    request<MarketingTemplate>('/marketing/templates', { method: 'POST', body: data }),
  updateTemplate: (id: number, data: Partial<MarketingTemplate>) =>
    request<MarketingTemplate>(`/marketing/templates/${id}`, { method: 'PUT', body: data }),
  deleteTemplate: (id: number) =>
    request<{ status: string }>(`/marketing/templates/${id}`, { method: 'DELETE' }),

  // Emails (review queue)
  listReviewQueue: (params?: { page?: number; per_page?: number }) =>
    requestPaginated<MarketingEmail>(`/marketing/emails${buildQueryString(params ?? {})}`),
  getEmail: (id: number) => request<MarketingEmail>(`/marketing/emails/${id}`),
  updateEmail: (id: number, data: { subject: string; preheader: string; body_html: string }) =>
    request<MarketingEmail>(`/marketing/emails/${id}`, { method: 'PUT', body: data }),
  approveEmail: (id: number) =>
    request<MarketingEmail>(`/marketing/emails/${id}/approve`, { method: 'POST' }),
  rejectEmail: (id: number) =>
    request<{ status: string }>(`/marketing/emails/${id}/reject`, { method: 'POST' }),
  regenerateEmail: (id: number, data: { template_id: number }) =>
    request<{ status: string }>(`/marketing/emails/${id}/regenerate`, { method: 'POST', body: data }),

  // Suppressions
  listSuppressions: (params?: { page?: number; per_page?: number }) =>
    requestPaginated<MarketingSuppression>(`/marketing/suppressions${buildQueryString(params ?? {})}`),
}

export const versions = {
  get: async () => {
    const base = import.meta.env.VITE_API_URL || ''
    const res = await fetch(`${base}/public/v1/versions`)
    if (!res.ok) throw new Error('Failed to fetch versions')
    return res.json() as Promise<{
      api: string
      dashboard: string
      admin: string
      sdk: string
      docs: string
    }>
  },
}

// ---------------------------------------------------------------------------
// Blacklist
// ---------------------------------------------------------------------------

export interface BlacklistEntry {
  id: number
  tenant_id: number
  reason: string
  source: string
  token: string | null
  added_by: number | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export const blacklist = {
  list: (params?: { page?: number; per_page?: number; tenant_id?: number }) =>
    requestPaginated<BlacklistEntry>(`/blacklist${buildQueryString(params ?? {})}`),
  add: (data: { tenant_id: number; embedding: number[]; reason: string }) =>
    request<BlacklistEntry>('/blacklist', { method: 'POST', body: data }),
  remove: (id: number, tenantId: number) =>
    request<{ message: string }>(`/blacklist/${id}?tenant_id=${tenantId}`, { method: 'DELETE' }),
}

// ---------------------------------------------------------------------------
// DPA
// ---------------------------------------------------------------------------

export interface DPAAcceptance {
  id: number
  tenant_id: number
  version: string
  accepted_by: number
  accepted_at: string
  ip_address: string
  user_agent: string
  signatory_name: string
  signatory_title: string
  company_legal_name: string
  document_hash: string
}

export interface DPAStats {
  total_accepted: number
  total_pending: number
  acceptance_rate: number
  current_version: string
}

export const dpa = {
  stats: () => request<DPAStats>('/dpa/stats'),
  acceptances: (params?: { page?: number; per_page?: number; version?: string }) =>
    requestPaginated<DPAAcceptance>(`/dpa/acceptances${buildQueryString(params ?? {})}`),
  getAcceptance: (id: number) => request<DPAAcceptance>(`/dpa/acceptances/${id}`),
  pending: (params?: { page?: number; per_page?: number }) =>
    requestPaginated<Tenant>(`/dpa/pending${buildQueryString(params ?? {})}`),
  remind: (tenantId: number) =>
    request<{ message: string }>('/dpa/remind', { method: 'POST', body: { tenant_id: tenantId } }),
  remindAll: () =>
    request<{ message: string; reminders_sent: number; total_pending: number }>('/dpa/remind-all', { method: 'POST' }),
}

// ---------------------------------------------------------------------------
// Review Queue
// ---------------------------------------------------------------------------

export interface ReviewQueueItem {
  id: number
  token: string
  tenant_id: number
  account_id?: number
  status: string
  priority: string
  reason: string
  flag_source: string
  min_age: number
  document_front_key?: string
  document_back_key?: string
  selfie_key?: string
  assigned_to: number | null
  resolved_by?: number
  reviewer_notes: string
  resolved_at: string | null
  created_at: string
  updated_at: string
}

export interface ReviewDocuments {
  front_url?: string
  back_url?: string
  selfie_url?: string
}

export interface ReviewQueueStats {
  pending: number
  in_progress: number
  approved: number
  rejected: number
  total: number
}

export const reviewQueue = {
  list: (params?: { page?: number; per_page?: number; status?: string; priority?: string }) =>
    requestPaginated<ReviewQueueItem>(`/review-queue${buildQueryString(params ?? {})}`),
  stats: () => request<ReviewQueueStats>('/review-queue/stats'),
  get: (id: number) => request<ReviewQueueItem>(`/review-queue/${id}`),
  documents: (id: number) => request<ReviewDocuments>(`/review-queue/${id}/documents`),
  assign: (id: number, data?: { admin_user_id?: number }) =>
    request<{ message: string }>(`/review-queue/${id}/assign`, { method: 'PATCH', body: data ?? {} }),
  resolve: (id: number, data: { status: string; notes?: string }) =>
    request<{ message: string }>(`/review-queue/${id}/resolve`, { method: 'PATCH', body: data }),
}

// ---------------------------------------------------------------------------
// Surveys
// ---------------------------------------------------------------------------

export interface SurveyListItem {
  id: number
  slug: string
  title: string
  status: string
  total_responses: number
  created_at: string
}

export interface SurveyQuestionOption {
  value: string
  label: string
}

export interface SurveyQuestion {
  id?: string
  label?: string
  title?: string
  type?: string
  options?: SurveyQuestionOption[]
}

/** Author-defined survey structure. Only `questions` is consumed by the UI. */
export interface SurveySchema {
  questions?: SurveyQuestion[]
}

/**
 * One question's aggregated answers. The shape depends on the question type
 * (rating → average+distribution, free text → count+samples, choice →
 * value→count map), so consumers must narrow before rendering.
 */
export type SurveyAggregationEntry = Record<string, unknown>

export interface SurveyDetailResponse {
  id: number
  slug: string
  title: string
  description: string
  status: string
  schema: SurveySchema
  total_responses: number
  aggregation: {
    total_responses: number
    aggregation: Record<string, SurveyAggregationEntry>
    computed_at: string
  } | null
  created_at: string
}

export interface SurveyResponseItem {
  id: number
  survey_id: number
  answers: Record<string, unknown>
  created_at: string
}

export const surveys = {
  list: (params?: { page?: number; per_page?: number; search?: string }) =>
    requestPaginated<SurveyListItem>(`/surveys${buildQueryString(params ?? {})}`),

  get: (id: number) => request<SurveyDetailResponse>(`/surveys/${id}`),

  create: (data: { slug: string; title: string; description: string; schema: SurveySchema; status: string }) =>
    request<SurveyDetailResponse>('/surveys', { method: 'POST', body: data }),

  update: (id: number, data: Record<string, unknown>) =>
    request<SurveyDetailResponse>(`/surveys/${id}`, { method: 'PUT', body: data }),

  responses: (id: number, params?: { page?: number; per_page?: number }) =>
    requestPaginated<SurveyResponseItem>(`/surveys/${id}/responses${buildQueryString(params ?? {})}`),

  recompute: (id: number) =>
    request<{ message?: string }>(`/surveys/${id}/recompute`, { method: 'POST' }),
}

// ---------------------------------------------------------------------------
// RBAC Roles
// ---------------------------------------------------------------------------

export interface AdminRole {
  id: number
  name: string
  description: string
  is_system: boolean
  permissions: string[]
  permission_count: number
  user_count: number
  created_at: string
  updated_at: string
}

export interface PermissionGroup {
  name: string
  description: string
  permissions: string[]
}

export const roles = {
  list: () => request<AdminRole[]>('/roles'),
  get: (id: number) => request<AdminRole>(`/roles/${id}`),
  create: (data: { name: string; description: string; permissions: string[] }) =>
    request<AdminRole>('/roles', { method: 'POST', body: data }),
  update: (id: number, data: { name: string; description: string; permissions: string[] }) =>
    request<AdminRole>(`/roles/${id}`, { method: 'PUT', body: data }),
  delete: (id: number) =>
    request<{ message: string }>(`/roles/${id}`, { method: 'DELETE' }),
  listPermissions: () => request<PermissionGroup[]>('/permissions'),
  assignRole: (userId: number, roleId: number) =>
    request<{ message: string }>(`/users/admin/${userId}/role`, { method: 'PATCH', body: { role_id: roleId } }),
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export interface FeedbackItem {
  id: number
  source: string
  account_id: number | null
  rating: number
  comment: string
  verification_outcome: string
  user_agent: string
  created_at: string
}

export interface FeedbackStats {
  total_count: number
  average_rating: number
  portal_count: number
  widget_count: number
  by_rating: Record<string, number>
}

export const feedback = {
  list: (params?: Record<string, string | number | boolean | undefined>) =>
    requestPaginated<FeedbackItem>(`/feedback${buildQueryString(params ?? {})}`),

  stats: () => request<FeedbackStats>('/feedback/stats'),
}

// ---------------------------------------------------------------------------
// Content Categories
// ---------------------------------------------------------------------------

export const contentCategories = {
  list: (params?: { search?: string }) =>
    request<ContentCategory[]>(`/content-categories${buildQueryString(params ?? {})}`),

  get: (id: number) => request<ContentCategory>(`/content-categories/${id}`),

  create: (data: Partial<ContentCategory>) =>
    request<ContentCategory>('/content-categories', { method: 'POST', body: data }),

  update: (id: number, data: Partial<ContentCategory>) =>
    request<ContentCategory>(`/content-categories/${id}`, { method: 'PUT', body: data }),

  delete: (id: number) =>
    request<void>(`/content-categories/${id}`, { method: 'DELETE' }),

  listCountryRules: (id: number, params?: { page?: number; per_page?: number; search?: string }) =>
    requestPaginated<CountryRule>(`/content-categories/${id}/country-rules${buildQueryString(params ?? {})}`),

  createCountryRule: (id: number, data: Partial<CountryRule>) =>
    request<CountryRule>(`/content-categories/${id}/country-rules`, { method: 'POST', body: data }),

  updateCountryRule: (ruleId: number, data: Partial<CountryRule>) =>
    request<CountryRule>(`/country-rules/${ruleId}`, { method: 'PUT', body: data }),

  deleteCountryRule: (ruleId: number) =>
    request<void>(`/country-rules/${ruleId}`, { method: 'DELETE' }),
}

// ---------------------------------------------------------------------------
// VLM OCR Providers (document-OCR consensus routing & spend)
// ---------------------------------------------------------------------------

export interface VLMProvider {
  id: number
  name: string
  kind: string // 'openai_compat' | 'anthropic' | 'mistral_ocr'
  base_url: string
  has_key: boolean
  key_fingerprint?: string // sha256[:12] of the stored key — never the key itself
  enabled: boolean
  // NOTE: no rates here. A price belongs to a (provider, model) pair — see
  // VLMProviderModel. One provider serving two models at one rate was the bug.
  balance_source: string // 'none' | 'live' | 'manual'
  manual_balance_usd?: number
  last_balance_usd?: number
  last_synced_at?: string
  extra_body?: Record<string, unknown> // merged into the request body (reasoning-model flags, etc.)
  created_at: string
  updated_at: string
}

// One model offered by one provider, with the rates it costs ON THAT PROVIDER.
// This is the priced unit and the only thing a route can point at.
export interface VLMProviderModel {
  id: number
  provider_id: number
  provider?: VLMProvider
  model: string // provider-native ID, e.g. 'vertex/gemini-3.1-flash-lite@eu'
  in_per_mtok: number
  out_per_mtok: number
  price_per_call: number // USD per successful call — per-request/page billing (Mistral OCR); 0 = token-billed
  enabled: boolean
  /** Model-level request extras, merged OVER the provider's (model keys win).
   *  Reasoning switches such as {"reasoning_effort":"none"} live here, not on
   *  the provider: a router serves many models behind one host. */
  extra_body?: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface VLMProviderModelInput {
  model: string
  in_per_mtok: number
  out_per_mtok: number
  price_per_call: number
  enabled: boolean
  extra_body?: Record<string, unknown>
}

export interface VLMRoute {
  id: number
  slot: string // 'vlm_a' | 'vlm_b'
  provider_model_id: number
  provider_model?: VLMProviderModel
  model: string // mirrored from provider_model — never free text
  provider?: VLMProvider // mirrored from provider_model.provider
  enabled: boolean
  created_at: string
  updated_at: string
}

// One (provider, model) pair's usage priced at THAT pair's rates.
export interface VLMModelUsageRow {
  model: string
  calls: number
  error_calls: number
  avg_latency_ms: number
  input_tokens: number
  output_tokens: number
  est_cost_usd: number
  priced: boolean // false = no rate row matched, so cost reads 0 (not "free")
}

export interface VLMUsageRow {
  provider_name: string
  calls: number
  error_calls: number
  avg_latency_ms: number
  input_tokens: number
  output_tokens: number
  est_cost_usd: number
  models: VLMModelUsageRow[]
}

export interface VLMFailure {
  created_at: string
  provider_name: string
  model: string
  error_detail: string
}

export interface VLMUsageSummary {
  from: string
  to: string
  providers: VLMUsageRow[]
}

// Create/update payload. api_key is write-only: omit (or send empty) on update to
// keep the stored key.
export interface VLMProviderInput {
  name: string
  kind?: string
  base_url: string
  api_key?: string
  enabled: boolean
  balance_source: string
  extra_body?: Record<string, unknown> // null/omit clears it
}

export const vlmProviders = {
  list: () => request<VLMProvider[]>('/vlm/providers'),

  get: (id: number) => request<VLMProvider>(`/vlm/providers/${id}`),

  create: (data: VLMProviderInput) =>
    request<VLMProvider>('/vlm/providers', { method: 'POST', body: data }),

  update: (id: number, data: VLMProviderInput) =>
    request<VLMProvider>(`/vlm/providers/${id}`, { method: 'PUT', body: data }),

  delete: (id: number) =>
    request<void>(`/vlm/providers/${id}`, { method: 'DELETE' }),

  setBalance: (id: number, balanceUsd: number) =>
    request<VLMProvider>(`/vlm/providers/${id}/balance`, {
      method: 'PATCH',
      body: { balance_usd: balanceUsd },
    }),

  // ── Provider models (the priced unit) ───────────────────────────
  listAllModels: () => request<VLMProviderModel[]>('/vlm/models'),

  listProviderModels: (providerId: number) =>
    request<VLMProviderModel[]>(`/vlm/providers/${providerId}/models`),

  createProviderModel: (providerId: number, data: VLMProviderModelInput) =>
    request<VLMProviderModel>(`/vlm/providers/${providerId}/models`, { method: 'POST', body: data }),

  updateProviderModel: (modelId: number, data: VLMProviderModelInput) =>
    request<VLMProviderModel>(`/vlm/models/${modelId}`, { method: 'PUT', body: data }),

  deleteProviderModel: (modelId: number) =>
    request<void>(`/vlm/models/${modelId}`, { method: 'DELETE' }),

  listRoutes: () => request<VLMRoute[]>('/vlm/routes'),

  // A slot points at an EXISTING provider-model — there is no free-text model,
  // so a leg can never run on something that has no price of its own.
  upsertRoute: (slot: string, data: { provider_model_id: number; enabled: boolean }) =>
    request<VLMRoute>(`/vlm/routes/${slot}`, { method: 'PUT', body: data }),

  usage: (params?: { from?: string; to?: string }) =>
    request<VLMUsageSummary>(`/vlm/usage${buildQueryString(params ?? {})}`),
  failures: (params?: { limit?: number }) =>
    request<{ failures: VLMFailure[] }>(`/vlm/failures${buildQueryString(params ?? {})}`),
}

// ---------------------------------------------------------------------------
// Promos / Coupons
// ---------------------------------------------------------------------------

export interface PromoCode {
  id: number
  code: string
  description?: string
  discount_type: string
  credit_cents: number
  max_uses?: number
  current_uses: number
  valid_from: string
  valid_until?: string
  credit_expires_days?: number
  eligible_plans: string[]
  created_by?: number
  is_active: boolean
  created_at: string
  updated_at: string
  free_event_count?: number
  applicable_event_types: string[]
  percent_discount?: number
  duration_months?: number
  campaign?: string
  channel?: string
}

export interface PromoRedemption {
  id: number
  promo_code_id: number
  tenant_id: number
  credit_cents: number
  redeemed_at: string
  free_events_granted?: number
  free_events_used: number
  percent_discount?: number
  discount_expires_at?: string
  applicable_event_types: string[]
}

export const promos = {
  create: (data: {
    code: string
    description?: string
    discount_type?: string
    credit_cents?: number
    free_event_count?: number
    applicable_event_types?: string[]
    percent_discount?: number
    duration_months?: number
    max_uses?: number
    valid_until?: string
    credit_expires_days?: number
    eligible_plans?: string[]
    campaign?: string
    channel?: string
  }) => request<PromoCode>('/promos', { method: 'POST', body: data }),

  list: (params?: { page?: number; per_page?: number }) =>
    requestPaginated<PromoCode>(`/promos${buildQueryString(params ?? {})}`),

  get: (id: number) => request<PromoCode>(`/promos/${id}`),

  update: (id: number, data: Record<string, unknown>) =>
    request<PromoCode>(`/promos/${id}`, { method: 'PATCH', body: data }),

  listRedemptions: (id: number, params?: { page?: number; per_page?: number }) =>
    requestPaginated<PromoRedemption>(`/promos/${id}/redemptions${buildQueryString(params ?? {})}`),
}
